import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { AbstractControl, FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyFieldConfig, FormlyFormOptions, FormlyModule } from '@ngx-formly/core';
import { Subscription } from 'rxjs';
import { LoggerService } from '../../../../core/services/logger.service';
import { LookupListService } from '../../../../core/services/lookup-list.service';
import { TranslationService } from '../../../../core/services/translation.service';
import { IconComponent } from '../../../../shared/components/icon/icon.component';
import { FieldEditBase } from '../field-edit-base';

interface BuilderFieldOption {
  id: string;
  type: string;
  label: string;
  allowMultiple?: boolean;
  optionsSource?: 'inline' | 'lookup-list';
  lookupListId?: string;
  options?: Array<{ value?: unknown; label?: unknown }>;
}

const CALCULATED_METADATA_SOURCES = {
  previousSubmissionTime: '__meta.previous_submission_time',
  now: '__meta.now',
} as const;

/**
 * Generic field config editor component driven by Formly definitions.
 *
 * Replaces per-type custom edit components with a single generic component that receives
 * its form definition as input. The form definition determines which fields are shown
 * and how they behave.
 *
 * Architecture:
 * - Receives `configFormFields` (FormlyFieldConfig[]) as input from parent
 * - Renders a Formly form with the provided definition
 * - Model is the FieldDefinition being edited (two-way bound via fieldSignal)
 * - Detects type changes via (modelChange) and delegates to onFieldTypeChanged()
 * - Validates via Formly form validity and custom logic in saveChanges()
 *
 * Usage:
 * ```typescript
 * const config: ModalConfig = {
 *   component: GenericFieldConfigEditorComponent,
 *   componentInputs: {
 *     fieldSignal,
 *     configFormFields: getTextFieldConfigFields(), // Formly definition
 *     saveDisabled,
 *     cancelDisabled,
 *     typeChangeSignal,
 *   },
 *   // ... modal config
 * };
 * ```
 */
@Component({
  selector: 'app-generic-field-config-editor',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormlyModule, IconComponent],
  templateUrl: './generic-field-config-editor.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './generic-field-config-editor.component.css',
})
export class GenericFieldConfigEditorComponent extends FieldEditBase implements OnInit, OnDestroy {
  private translationService = inject(TranslationService);
  private lookupListService = inject(LookupListService);
  // Use a component-specific logger namespace, overriding the base class's 'field-edit-base' namespace
  protected override log = inject(LoggerService).getLogger('generic-field-editor');

  /**
   * The Formly field config array for this field type's config editor.
   * Provided by FormBuilderComponent based on the field type being edited.
   */
  @Input() configFormFields: FormlyFieldConfig[] = [];
  @Input() availableFields: BuilderFieldOption[] = [];

  configForm = new FormGroup({});

  /** Formly options to control validation display behavior */
  configFormOptions: FormlyFormOptions = {
    formState: {
      showError: (field: any) => {
        // Only show validation errors after field has been touched or dirty
        return field.formControl?.touched || field.formControl?.dirty;
      },
      // Populated reactively below; used by LOOKUP_LIST_ID_FIELD's expressionProperties
      lookupListOptions: [] as { value: string; label: string }[],
      calculatedDateFieldOptions: [] as { value: string; label: string }[],
      calculatedNumberFieldOptions: [] as { value: string; label: string }[],
      calculatedChecklistFieldOptions: [] as { value: string; label: string }[],
      calculatedStateSourceFieldOptions: [] as { value: string; label: string }[],
      calculatedStateOutputFieldOptions: [] as { value: string; label: string }[],
      calculatedStateTransitionValueOptionsByFieldId: {} as Record<
        string,
        { value: string; label: string }[]
      >,
      visibilityFieldOptions: [] as { value: string; label: string }[],
      visibilityValueOptionsByFieldId: {} as Record<string, { value: string; label: string }[]>,
    },
  };

  constructor() {
    super();
    // Keep formState.lookupListOptions in sync with the active lookup lists.
    const activeQuery = this.lookupListService.createActiveQuery();
    effect(() => {
      const lists = activeQuery.data();
      this.configFormOptions.formState!['lookupListOptions'] = lists.map((l) => ({
        value: l.id,
        label: l.name,
      }));
      this.lookupListsById = Object.fromEntries(
        lists.map((list) => [
          list.id,
          (list.options ?? [])
            .map((option) => ({
              value: String(option.value ?? '').trim(),
              label: String(option.label ?? option.value ?? '').trim(),
            }))
            .filter((option) => option.value !== ''),
        ]),
      );
      this.configureCalculatedFieldOptions();
    });
  }

  /** Track current type to detect changes from the field-type-selector Formly field */
  private lastKnownType: string | null = null;

  /** Track the controlling field for visibility so we can clear values when it changes */
  private lastKnownVisibilityFieldId: string | null = null;

  /** Guards onModelChange clearing logic from firing during Formly's initialization model changes */
  private isInitialized = false;

  /** Translated config form fields (labels/placeholders are translated from keys) */
  translatedConfigFormFields: FormlyFieldConfig[] = [];

  /** Subscription to form status changes for reactive validation */
  private formStatusSubscription?: Subscription;
  private lookupListsById: Record<string, Array<{ value: string; label: string }>> = {};

  override ngOnInit(): void {
    super.ngOnInit();

    const field = this.field();
    if (field) {
      this.lastKnownType = field.type;
      this.ensureStateTransitionRuleDefaults();
      this.configureCalculatedFieldOptions();
      this.configureVisibilityConditionOptions();

      if (!field.visibleWhenMode) {
        field.visibleWhenMode = field.visibleWhen ? 'show_when_value' : 'none';
      }

      this.lastKnownVisibilityFieldId = field.visibleWhen?.fieldId ?? null;

      if (field.visibleWhen && Array.isArray(field.visibleWhen.values)) {
        const originField = this.availableFields.find((f) => f.id === field.visibleWhen?.fieldId);
        const knownOptions = this.getVisibilityValueOptions(originField);
        if (knownOptions.length > 0) {
          field.visibleWhen = {
            fieldId: field.visibleWhen.fieldId,
            values: '',
            selectedValues: field.visibleWhen.values as string[],
          };
        } else {
          field.visibleWhen = {
            fieldId: field.visibleWhen.fieldId,
            values: field.visibleWhen.values.join(', '),
          };
        }
      }

      // Pre-populate default options for new multi-option fields so the user sees
      // two editable starter options instead of an empty list.
      // However, don't do this for fields using lookup lists (options come from the lookup list, not inline).
      const MULTI_OPTION_TYPES = ['select', 'radio', 'checkbox-group', 'dropdown'];
      if (MULTI_OPTION_TYPES.includes(field.type)) {
        // Only set defaults if: options is empty AND optionsSource is NOT 'lookup-list'
        // (If optionsSource is 'lookup-list', options should remain empty since they come from the list)
        if (
          (!Array.isArray(field.options) || field.options.length === 0) &&
          field.optionsSource !== 'lookup-list'
        ) {
          this.setFieldValue('options', [
            { label: 'One', value: 'one' },
            { label: 'Two', value: 'two' },
          ]);
        }
      }
    }

    const configFieldsWithVisibility =
      this.configFormFields.length > 0
        ? this.mergeVisibilitySectionIntoAdvanced(this.configFormFields)
        : this.configFormFields;

    // Formly 7 does not render a FieldArrayType (our `options-editor`) on first
    // paint when `hideExpression` is applied directly to it — the FormArray is
    // never bound. Instead of poking Formly imperatively, hide a wrapping
    // fieldGroup: the group toggles visibility reactively with `calculationKind`
    // while the options-editor inside it renders and binds normally.
    const normalizedConfigFields = configFieldsWithVisibility.map((configField) => {
      if (
        typeof configField.key === 'string' &&
        configField.key === 'transitions' &&
        configField.type === 'options-editor'
      ) {
        const { hideExpression, className, ...transitionsField } = configField as any;
        return {
          className,
          hideExpression,
          fieldGroup: [transitionsField as FormlyFieldConfig],
        } as FormlyFieldConfig;
      }
      return configField;
    });

    // Translate all labels, placeholders, and descriptions in the config form fields.
    this.translatedConfigFormFields = [...this.translateFormFields(normalizedConfigFields)];

    // Inject data-testid attributes onto each config field so tests can reliably
    // locate inputs as `{fieldType}-{key}-input`, `{fieldType}-{key}-checkbox`, etc.
    const fieldType = this.field()?.type ?? 'unknown';
    this.translatedConfigFormFields = this.addTestIdsToFields(
      this.translatedConfigFormFields,
      fieldType,
    );

    // Subscribe to form status changes to update validation state reactively
    // This ensures save button state stays in sync with Formly form validity
    if (this.saveDisabled) {
      this.formStatusSubscription = this.configForm.statusChanges.subscribe(() => {
        this.updateValidationState();
      });

      // Run initial validation after a brief delay to ensure Formly has initialized
      setTimeout(() => {
        this.updateValidationState();
      }, 0);
    }

    // Allow Formly's initialization model changes to settle before treating
    // fieldId changes in onModelChange as user-driven field switches.
    setTimeout(() => {
      this.isInitialized = true;
    }, 0);
  }

  ngOnDestroy(): void {
    // Clean up form status subscription
    this.formStatusSubscription?.unsubscribe();
  }

  /**
   * Recursively translate label, placeholder, and description properties in Formly field configs.
   * Assumes props.label, props.placeholder, and props.description contain translation keys.
   */
  private translateFormFields(fields: FormlyFieldConfig[]): FormlyFieldConfig[] {
    return fields.map((field) => {
      const translatedField = { ...field };

      if (translatedField.props) {
        translatedField.props = { ...translatedField.props };

        // Translate label if it looks like a translation key
        if (translatedField.props['label'] && typeof translatedField.props['label'] === 'string') {
          const labelKey = translatedField.props['label'];
          if (labelKey.includes('.')) {
            // Looks like a translation key (contains '.')
            translatedField.props['label'] = this.translationService.translate(labelKey);
          }
        }

        // Translate placeholder if it looks like a translation key
        if (
          translatedField.props['placeholder'] &&
          typeof translatedField.props['placeholder'] === 'string'
        ) {
          const placeholderKey = translatedField.props['placeholder'];
          if (placeholderKey.includes('.')) {
            translatedField.props['placeholder'] =
              this.translationService.translate(placeholderKey);
          }
        }

        // Translate description if it looks like a translation key
        if (
          translatedField.props['description'] &&
          typeof translatedField.props['description'] === 'string'
        ) {
          const descriptionKey = translatedField.props['description'];
          if (descriptionKey.includes('.')) {
            translatedField.props['description'] =
              this.translationService.translate(descriptionKey);
          }
        }

        // Translate option labels if they look like translation keys
        // Used by field types with static select options (e.g., text-label fontSize, fontWeight)
        if (Array.isArray(translatedField.props['options'])) {
          translatedField.props['options'] = (
            translatedField.props['options'] as Array<{ value: unknown; label: string }>
          ).map((opt) => {
            if (opt.label && typeof opt.label === 'string' && opt.label.includes('.')) {
              return { ...opt, label: this.translationService.translate(opt.label) };
            }
            return opt;
          });
        }
      }

      // Recursively translate nested fieldGroup and fieldArray
      if (translatedField.fieldGroup) {
        translatedField.fieldGroup = this.translateFormFields(translatedField.fieldGroup);
      }

      if (translatedField.fieldArray) {
        const rawFieldArray = translatedField.fieldArray;

        if (typeof rawFieldArray !== 'function') {
          const translatedFieldArray = { ...rawFieldArray };
          if (translatedFieldArray.fieldGroup) {
            translatedFieldArray.fieldGroup = this.translateFormFields(
              translatedFieldArray.fieldGroup,
            );
          }

          translatedField.fieldArray = translatedFieldArray;
        }
      }

      return translatedField;
    });
  }

  /**
   * Recursively inject `data-testid` attributes onto config form fields so that
   * tests can reliably locate them using a predictable convention:
   *
   *   `{editingFieldType}-{key}-{suffix}`
   *
   * Where suffix is:
   *   - `input`    for text / number / email / pattern config inputs
   *   - `checkbox` for boolean toggle config fields
   *   - `select`   for dropdown config fields
   *
   * Examples:
   *   `text-label-input`, `text-required-checkbox`, `text-width-select`
   *   `textarea-rows-input`, `number-min-input`
   */
  private addTestIdsToFields(fields: FormlyFieldConfig[], fieldType: string): FormlyFieldConfig[] {
    return fields.map((f) => {
      const updated = { ...f };

      if (updated.key && typeof updated.key === 'string') {
        const key = updated.key;
        let suffix: string;
        switch (updated.type) {
          case 'checkbox':
            suffix = 'checkbox';
            break;
          case 'select':
            suffix = 'select';
            break;
          default:
            suffix = 'input';
            break;
        }
        updated.props = {
          ...updated.props,
          attributes: {
            ...(updated.props?.['attributes'] ?? {}),
            'data-testid': `${fieldType}-${key}-${suffix}`,
          },
        };
      }

      // Recurse into fieldGroup (e.g. advancedSection collapsible wrapper)
      if (updated.fieldGroup) {
        updated.fieldGroup = this.addTestIdsToFields(updated.fieldGroup, fieldType);
      }

      return updated;
    });
  }

  /**
   * Folds the conditional-visibility fields into the field type's existing
   * "Advanced Properties" collapsible section (identified by the shared
   * `collapsible-section` wrapper from `advancedSection()`) so visibility
   * lives alongside width, etc. rather than as its own top-level section.
   * Field types that don't define an advanced section (none currently) fall
   * back to appending it as a standalone section.
   */
  private mergeVisibilitySectionIntoAdvanced(fields: FormlyFieldConfig[]): FormlyFieldConfig[] {
    const visibilitySection = this.buildVisibilitySectionField();
    const advancedIndex = fields.findIndex((f) => f.wrappers?.includes('collapsible-section'));

    if (advancedIndex === -1) {
      return [...fields, visibilitySection];
    }

    const advancedField = fields[advancedIndex];
    const merged = [...fields];
    merged[advancedIndex] = {
      ...advancedField,
      fieldGroup: [...(advancedField.fieldGroup ?? []), ...(visibilitySection.fieldGroup ?? [])],
    };
    return merged;
  }

  private buildVisibilitySectionField(): FormlyFieldConfig {
    return {
      className: 'flex-basis-full',
      fieldGroup: [
        {
          key: 'visibleWhenMode',
          type: 'select',
          className: 'flex-basis-full',
          defaultValue: 'none',
          props: {
            label: 'formBuilder.visibility.modeLabel',
            options: [
              { value: 'none', label: 'formBuilder.visibility.mode.none' },
              {
                value: 'show_when_value',
                label: 'formBuilder.visibility.mode.showWhenValue',
              },
            ],
          },
        },
        {
          key: 'visibleWhen.fieldId',
          type: 'select',
          className: 'flex-basis-full',
          props: {
            label: 'formBuilder.visibility.fieldLabel',
            options: [],
          },
          expressionProperties: {
            'props.options': 'formState.visibilityFieldOptions',
          },
          hideExpression: (model: any) => model?.visibleWhenMode !== 'show_when_value',
        },
        {
          key: 'visibleWhen.selectedValues',
          type: 'multi-checkbox',
          className: 'flex-basis-full',
          props: {
            label: 'formBuilder.visibility.valuesLabel',
            options: [],
          },
          expressionProperties: {
            'props.options': (model: any, formState: any) => {
              const fieldId = model?.visibleWhen?.fieldId;
              if (!fieldId) return [];
              return (
                (
                  formState?.visibilityValueOptionsByFieldId as Record<
                    string,
                    { value: string; label: string }[]
                  >
                )?.[fieldId] ?? []
              );
            },
          },
          hideExpression: (model: any, formState: any) => {
            if (model?.visibleWhenMode !== 'show_when_value') return true;
            const fieldId = model?.visibleWhen?.fieldId;
            if (!fieldId) return true;
            const opts = (
              formState?.visibilityValueOptionsByFieldId as Record<
                string,
                { value: string; label: string }[]
              >
            )?.[fieldId];
            return !Array.isArray(opts) || opts.length === 0;
          },
        },
        {
          key: 'visibleWhen.values',
          type: 'text',
          className: 'flex-basis-full',
          props: {
            label: 'formBuilder.visibility.valuesLabel',
            placeholder: 'formBuilder.visibility.valuesPlaceholder',
            description: 'formBuilder.visibility.valuesDescription',
          },
          hideExpression: (model: any, formState: any) => {
            if (model?.visibleWhenMode !== 'show_when_value') return true;
            const fieldId = model?.visibleWhen?.fieldId;
            if (!fieldId) return false;
            const opts = (
              formState?.visibilityValueOptionsByFieldId as Record<
                string,
                { value: string; label: string }[]
              >
            )?.[fieldId];
            return Array.isArray(opts) && opts.length > 0;
          },
        },
      ],
    };
  }

  private isStateTransitionSourceField(field: BuilderFieldOption): boolean {
    return ['checkbox-group', 'radio', 'select', 'dropdown'].includes(field.type);
  }

  private getFieldDisplayLabel(field: BuilderFieldOption): string {
    const label = field.label?.trim();
    if (label) {
      return label;
    }

    return field.type?.trim() || this.translationService.translate('formBuilder.newField');
  }

  private getStateTransitionValueOptions(
    field: BuilderFieldOption,
  ): { value: string; label: string }[] {
    if (Array.isArray(field.options) && field.options.length > 0) {
      return field.options
        .map((option) => ({
          value: String(option.value ?? '').trim(),
          label: String(option.label ?? option.value ?? '').trim(),
        }))
        .filter((option) => option.value !== '');
    }

    if (field.optionsSource === 'lookup-list' && field.lookupListId) {
      return this.lookupListsById[field.lookupListId] ?? [];
    }

    return [];
  }

  private getVisibilityValueOptions(
    field: BuilderFieldOption | undefined,
  ): { value: string; label: string }[] {
    if (!field || !this.isStateTransitionSourceField(field)) return [];
    return this.getStateTransitionValueOptions(field);
  }

  private configureVisibilityConditionOptions(): void {
    const currentField = this.field();
    if (!currentField) {
      return;
    }

    const eligible = this.availableFields.filter((f) => f.id && f.id !== currentField.id);

    this.configFormOptions.formState!['visibilityFieldOptions'] = eligible.map((f) => ({
      value: f.id,
      label: f.label || f.id,
    }));

    const valueOptionsByFieldId: Record<string, { value: string; label: string }[]> = {};
    for (const f of eligible) {
      const opts = this.getVisibilityValueOptions(f);
      if (opts.length > 0) {
        valueOptionsByFieldId[f.id] = opts;
      }
    }
    this.configFormOptions.formState!['visibilityValueOptionsByFieldId'] = valueOptionsByFieldId;
  }

  /**
   * Called by Formly when model changes.
   * Detects type changes and triggers the type change flow via onFieldTypeChanged().
   * For type changes, skip validation and immediately trigger the signal.
   */
  onModelChange(): void {
    this.log.debug('[GenericFieldConfigEditor.onModelChange] 🔵 Entry - model change detected');

    const field = this.field();

    if (!field) {
      this.log.debug('[GenericFieldConfigEditor.onModelChange] ⚠️ No field - returning');
      return;
    }

    this.log.debug('[GenericFieldConfigEditor.onModelChange] 📊 Current field.type:', field.type);
    this.log.debug(
      '[GenericFieldConfigEditor.onModelChange] 📊 Last known type:',
      this.lastKnownType,
    );
    this.log.debug(
      '[GenericFieldConfigEditor.onModelChange] 📊 Type comparison:',
      field.type,
      '!==',
      this.lastKnownType,
      '=',
      field.type !== this.lastKnownType,
    );

    // Check if type changed - this takes priority over validation
    if (field.type !== this.lastKnownType) {
      this.log.debug(
        `[GenericFieldConfigEditor.onModelChange] 🔄 Type changed from ${this.lastKnownType} to ${field.type}`,
      );

      this.log.debug(
        '[GenericFieldConfigEditor.onModelChange] 📊 Checking typeChangeSignal:',
        this.typeChangeSignal,
      );
      if (this.typeChangeSignal === null || this.typeChangeSignal === undefined) {
        this.log.error(
          '[GenericFieldConfigEditor.onModelChange] ⚠️ WARNING: typeChangeSignal is NULL/UNDEFINED!',
        );
      } else {
        this.log.debug('[GenericFieldConfigEditor.onModelChange] ✅ typeChangeSignal is defined');
      }

      // Immediately trigger type change signal (bypasses validation)
      this.log.debug(
        '[GenericFieldConfigEditor.onModelChange] 🚀 Calling onFieldTypeChanged with type:',
        field.type,
      );
      this.onFieldTypeChanged(field.type);
      this.log.debug(
        '[GenericFieldConfigEditor.onModelChange] ✅ onFieldTypeChanged() call completed',
      );

      // Update lastKnownType AFTER processing the change
      // This ensures onFieldTypeChanged sees the old vs new type correctly in field.type
      this.lastKnownType = field.type;

      // Don't run validation when type changes - parent will close modal and reopen with new editor
      return;
    }

    // For non-type changes, the form statusChanges subscription will handle validation updates
    if (field.type === 'calculated') {
      this.ensureStateTransitionRuleDefaults();
      this.configureCalculatedFieldOptions();
    }

    this.configureVisibilityConditionOptions();

    if (field.visibleWhenMode === 'show_when_value') {
      const currentVisibilityFieldId = field.visibleWhen?.fieldId ?? null;
      if (currentVisibilityFieldId !== this.lastKnownVisibilityFieldId) {
        this.lastKnownVisibilityFieldId = currentVisibilityFieldId;
        // Only reset values when the user actively switches the controlling field
        // (not during Formly's initialization model changes)
        if (this.isInitialized && currentVisibilityFieldId) {
          this.setFieldValue('visibleWhen', {
            fieldId: currentVisibilityFieldId,
            values: '',
            selectedValues: [],
          });
        }
      }
    }
    this.log.debug(
      '[GenericFieldConfigEditor.onModelChange] 📝 No type change - validation will update via subscription',
    );
  }

  /**
   * Returns the current validation message only when at least one *invalid* control has
   * been touched or dirtied by the user.
   *
   * This follows the standard "blur-per-field" validation UX pattern:
   * - Per-field inline errors appear when the user leaves an invalid field (handled by
   *   configFormOptions.formState.showError).
   * - The global summary banner appears only once the user has directly interacted with
   *   (and left) a field that is still invalid — NOT just because they filled in some
   *   other valid field.
   *
   * The save button remains disabled (via saveDisabled signal) even when this returns null,
   * so the user is silently blocked until all fields are valid — no premature error noise.
   */
  get validationMessage(): string | null {
    if (!this.saveDisabled) return null;
    const msg = this.saveDisabled();
    if (!msg) return null;
    // Only show the banner once an invalid control has been touched/dirtied
    return this.hasInvalidTouchedControl(this.configForm) ? msg : null;
  }

  /**
   * Returns true if any control in the group (recursively) is both invalid and touched/dirty.
   * Used to guard the global validation banner — we only surface it once the user has
   * directly interacted with a field that remains invalid.
   */
  private hasInvalidTouchedControl(control: AbstractControl): boolean {
    if (control instanceof FormGroup) {
      return Object.values(control.controls).some((child) => this.hasInvalidTouchedControl(child));
    }
    if (control instanceof FormArray) {
      return control.controls.some((child) => this.hasInvalidTouchedControl(child));
    }
    // Leaf control: invalid AND the user has interacted with it
    return control.invalid && (control.touched || control.dirty);
  }

  /**
   * Override updateValidationState to use Formly form validity.
   * Continuously monitors the Formly form state and updates saveDisabled signal.
   */
  public override updateValidationState(): void {
    // Only update signal in modal mode
    if (!this.saveDisabled) {
      return;
    }

    this.log.debug('[GenericFieldConfigEditor.updateValidationState] Checking form validity...');
    this.log.debug(
      '[GenericFieldConfigEditor.updateValidationState] Form valid:',
      this.configForm.valid,
    );
    this.log.debug(
      '[GenericFieldConfigEditor.updateValidationState] Form status:',
      this.configForm.status,
    );
    this.log.debug(
      '[GenericFieldConfigEditor.updateValidationState] Form errors:',
      this.configForm.errors,
    );

    // Check Formly form validity directly
    if (!this.configForm.valid) {
      // Collect which controls are invalid to assist debugging
      const invalidPaths: string[] = [];
      this.collectInvalidControls(this.configForm, '', invalidPaths);
      this.log.debug(
        '[GenericFieldConfigEditor.updateValidationState] ❌ Invalid controls:',
        invalidPaths,
      );
      this.saveDisabled.set('Please complete all required fields');
      return;
    }

    const calculatedDependencyError = this.validateCalculatedDependencyConfiguration();
    if (calculatedDependencyError) {
      this.saveDisabled.set(calculatedDependencyError);
      return;
    }

    const visibilityError = this.validateVisibilityCondition();
    if (visibilityError) {
      this.saveDisabled.set(visibilityError);
    } else {
      this.log.debug(
        '[GenericFieldConfigEditor.updateValidationState] ✅ Form valid - enabling save',
      );
      this.saveDisabled.set(null);
    }
  }

  /**
   * Override saveChanges to add final validation and key generation.
   * Called by save() when the save button is clicked.
   */
  protected override saveChanges(): void {
    const field = this.field();
    if (!field) return;

    this.normalizeVisibilityCondition(field);
    this.normalizeStateTransitionRules(field);

    // Final check - form should already be valid due to continuous monitoring
    if (!this.configForm.valid) {
      this.setError('Please complete all required fields');
      return;
    }

    const calculatedDependencyError = this.validateCalculatedDependencyConfiguration();
    if (calculatedDependencyError) {
      this.setError(calculatedDependencyError);
      return;
    }

    const visibilityError = this.validateVisibilityCondition();
    if (visibilityError) {
      this.setError(visibilityError);
      return;
    }

    this.log.debug('[GenericFieldConfigEditor] Validation passed for field:', field.label);
  }

  private normalizeVisibilityCondition(field: any): void {
    if (field.visibleWhenMode !== 'show_when_value') {
      field.visibleWhen = undefined;
      return;
    }

    const visibleWhen = field.visibleWhen;
    const fieldId = typeof visibleWhen?.fieldId === 'string' ? visibleWhen.fieldId : '';

    const knownOptions = (
      this.configFormOptions.formState!['visibilityValueOptionsByFieldId'] as Record<
        string,
        { value: string; label: string }[]
      >
    )?.[fieldId];
    const hasKnownOptions = Array.isArray(knownOptions) && knownOptions.length > 0;

    let values: string[];
    if (hasKnownOptions) {
      const raw = visibleWhen?.selectedValues;
      values = Array.isArray(raw)
        ? raw.map((v: unknown) => String(v ?? '')).filter((v: string) => v !== '')
        : [];
    } else {
      const valuesRaw = visibleWhen?.values;
      values = Array.isArray(valuesRaw)
        ? valuesRaw.map((v: unknown) => String(v ?? '')).filter((v: string) => v !== '')
        : typeof valuesRaw === 'string'
          ? valuesRaw
              .split(',')
              .map((v: string) => v.trim())
              .filter((v: string) => v !== '')
          : [];
    }

    if (!fieldId || values.length === 0) {
      field.visibleWhen = undefined;
      return;
    }

    field.visibleWhen = { fieldId, values };
  }

  private normalizeStateTransitionRules(field: any): void {
    if (field.type !== 'calculated' || field.calculationKind !== 'state_transition') {
      return;
    }

    const transitions = Array.isArray(field.transitions) ? field.transitions : [];
    field.transitions = transitions
      .map((rule: any) => {
        const fromRaw = rule?.from ?? rule?.label ?? '';
        const toRaw = rule?.to ?? rule?.value ?? '';
        const from = Array.isArray(fromRaw)
          ? fromRaw.map((value: unknown) => String(value ?? ''))
          : String(fromRaw)
              .split(',')
              .map((value) => value.trim());

        return {
          from,
          to: String(toRaw).trim(),
        };
      })
      .filter((rule: { from: string[]; to: string }) => rule.from.length > 0 || rule.to !== '');
  }

  private ensureStateTransitionRuleDefaults(): void {
    const currentField = this.field();
    if (
      !currentField ||
      currentField.type !== 'calculated' ||
      currentField.calculationKind !== 'state_transition'
    ) {
      return;
    }

    if (!Array.isArray(currentField.transitions) || currentField.transitions.length === 0) {
      // Use setFieldValue (new object reference) rather than mutating in place, so
      // Formly's `[model]` binding re-renders the transitions options-editor with
      // the starter row. The field's declarative `hideExpression` handles show/hide.
      this.setFieldValue('transitions', [{ from: [], to: '' }]);
    }
  }

  private configureCalculatedFieldOptions(): void {
    const currentField = this.field();
    if (!currentField || currentField.type !== 'calculated') {
      return;
    }

    const dateTypes = new Set(['date', 'datetime']);
    const numberTypes = new Set(['number']);
    const checklistTypes = new Set(['checkbox-group']);

    const eligible = this.availableFields.filter((f) => f.id && f.id !== currentField.id);
    const nonCalculated = eligible.filter((f) => f.type !== 'calculated');
    const dateFields = nonCalculated.filter((f) => dateTypes.has(f.type));
    const numberFields = nonCalculated.filter((f) => numberTypes.has(f.type));
    const checklistFields = nonCalculated.filter(
      (f) => checklistTypes.has(f.type) || (f.type === 'select' && f.allowMultiple === true),
    );

    const stateTransitionSources = nonCalculated.filter((f) =>
      this.isStateTransitionSourceField(f),
    );
    const stateTransitionValueOptionsByFieldId: Record<string, { value: string; label: string }[]> =
      {};

    for (const sourceField of stateTransitionSources) {
      stateTransitionValueOptionsByFieldId[sourceField.id] =
        this.getStateTransitionValueOptions(sourceField);
    }

    const metadataDateOptions = [
      {
        value: CALCULATED_METADATA_SOURCES.previousSubmissionTime,
        label: this.translationService.translate(
          'formBuilder.calculated.metadata.previousSubmissionTime',
        ),
      },
      {
        value: CALCULATED_METADATA_SOURCES.now,
        label: this.translationService.translate('formBuilder.calculated.metadata.now'),
      },
    ];

    this.configFormOptions.formState!['calculatedDateFieldOptions'] = [
      ...dateFields.map((f) => ({
        value: f.id,
        label: this.getFieldDisplayLabel(f),
      })),
      ...metadataDateOptions,
    ];
    this.configFormOptions.formState!['calculatedNumberFieldOptions'] = numberFields.map((f) => ({
      value: f.id,
      label: this.getFieldDisplayLabel(f),
    }));
    this.configFormOptions.formState!['calculatedChecklistFieldOptions'] = checklistFields.map(
      (f) => ({
        value: f.id,
        label: this.getFieldDisplayLabel(f),
      }),
    );
    this.configFormOptions.formState!['calculatedStateSourceFieldOptions'] =
      stateTransitionSources.map((f) => ({
        value: f.id,
        label: this.getFieldDisplayLabel(f),
      }));
    this.configFormOptions.formState!['calculatedStateOutputFieldOptions'] =
      stateTransitionSources.map((f) => ({
        value: f.id,
        label: this.getFieldDisplayLabel(f),
      }));
    this.configFormOptions.formState!['calculatedStateTransitionValueOptionsByFieldId'] =
      stateTransitionValueOptionsByFieldId;
  }

  private validateCalculatedDependencyConfiguration(): string | null {
    const field = this.field();
    if (!field || field.type !== 'calculated') {
      return null;
    }

    const byId = new Map(this.availableFields.map((f) => [f.id, f]));
    const currentFieldId = field.id;

    const validateFieldRef = (
      selectedId: string | undefined,
      validTypes: Set<string>,
      missingKey: string,
      invalidKey: string,
      validMetadataKeys?: Set<string>,
    ): string | null => {
      if (!selectedId) {
        return this.translationService.translate(missingKey);
      }

      if (selectedId === currentFieldId) {
        return this.translationService.translate('formBuilder.calculated.validation.selfReference');
      }

      if (validMetadataKeys?.has(selectedId)) {
        return null;
      }

      const target = byId.get(selectedId);
      if (!target) {
        return this.translationService.translate(invalidKey);
      }

      if (target.type === 'calculated') {
        return this.translationService.translate(
          'formBuilder.calculated.validation.noCalculatedDependencies',
        );
      }

      const supportsMultiSelect = validTypes.has('select-multiple');
      const isValidType =
        validTypes.has(target.type) ||
        (target.type === 'select' && supportsMultiSelect && target.allowMultiple === true);

      if (!isValidType) {
        return this.translationService.translate(invalidKey);
      }

      return null;
    };

    const dateTypes = new Set(['date', 'datetime']);
    const dateMetadata = new Set<string>(Object.values(CALCULATED_METADATA_SOURCES));
    const numberTypes = new Set(['number']);
    const checklistTypes = new Set(['checkbox-group', 'select-multiple']);

    if (field.calculationKind === 'date_diff_between_fields') {
      // date_diff_between_fields always resolves both fields with the
      // 'current' scope (see CalculatedFieldEvaluatorService.dateDiffBetweenFields),
      // so pairing the same field or metadata source with itself always
      // resolves both sides to the same value — the diff is always 0, never a
      // meaningful duration.
      if (
        field.inputFieldAId &&
        field.inputFieldBId &&
        field.inputFieldAId === field.inputFieldBId
      ) {
        return this.translationService.translate(
          'formBuilder.calculated.validation.dateDiffFieldsCannotBeIdentical',
        );
      }

      return (
        validateFieldRef(
          field.inputFieldAId,
          dateTypes,
          'formBuilder.calculated.validation.fieldARequired',
          'formBuilder.calculated.validation.dateFieldOnly',
          dateMetadata,
        ) ||
        validateFieldRef(
          field.inputFieldBId,
          dateTypes,
          'formBuilder.calculated.validation.fieldBRequired',
          'formBuilder.calculated.validation.dateFieldOnly',
          dateMetadata,
        )
      );
    }

    if (field.calculationKind === 'date_diff_from_previous') {
      // "Previous submission time" always resolves to the same value for both
      // the current and previous scope, so a diff against it is always zero (or
      // the empty fallback) — never a meaningful elapsed time. Block it here
      // rather than let it silently produce a useless calculated field.
      if (field.currentFieldId === CALCULATED_METADATA_SOURCES.previousSubmissionTime) {
        return this.translationService.translate(
          'formBuilder.calculated.validation.currentFieldCannotBePreviousSubmissionTime',
        );
      }

      return validateFieldRef(
        field.currentFieldId,
        dateTypes,
        'formBuilder.calculated.validation.currentFieldRequired',
        'formBuilder.calculated.validation.dateFieldOnly',
        dateMetadata,
      );
    }

    if (field.calculationKind === 'arithmetic_two_fields') {
      return (
        validateFieldRef(
          field.inputFieldAId,
          numberTypes,
          'formBuilder.calculated.validation.fieldARequired',
          'formBuilder.calculated.validation.numberFieldOnly',
        ) ||
        validateFieldRef(
          field.inputFieldBId,
          numberTypes,
          'formBuilder.calculated.validation.fieldBRequired',
          'formBuilder.calculated.validation.numberFieldOnly',
        )
      );
    }

    if (field.calculationKind === 'checklist_total_metric') {
      return validateFieldRef(
        field.checklistFieldId,
        checklistTypes,
        'formBuilder.calculated.validation.checklistFieldRequired',
        'formBuilder.calculated.validation.checklistFieldOnly',
      );
    }

    if (field.calculationKind === 'state_transition') {
      const stateSourceTypes = new Set(['checkbox-group', 'radio', 'select', 'dropdown']);

      const selectedId = field.sourceFieldId;
      if (!selectedId) {
        return this.translationService.translate(
          'formBuilder.calculated.validation.sourceFieldRequired',
        );
      }

      if (selectedId !== currentFieldId) {
        const target = byId.get(selectedId);
        if (!target) {
          return this.translationService.translate(
            'formBuilder.calculated.validation.stateTransitionSourceFieldOnly',
          );
        }

        if (target.type === 'calculated') {
          return this.translationService.translate(
            'formBuilder.calculated.validation.noCalculatedDependencies',
          );
        }

        if (!stateSourceTypes.has(target.type)) {
          return this.translationService.translate(
            'formBuilder.calculated.validation.stateTransitionSourceFieldOnly',
          );
        }
      }

      const transitions = Array.isArray(field.transitions) ? field.transitions : [];
      if (transitions.length === 0) {
        return this.translationService.translate(
          'formBuilder.calculated.validation.transitionsRequired',
        );
      }

      const outputFieldId =
        typeof field.outputFieldId === 'string' ? field.outputFieldId.trim() : '';
      if (outputFieldId) {
        const outputTarget = byId.get(outputFieldId);
        if (!outputTarget || !stateSourceTypes.has(outputTarget.type)) {
          return this.translationService.translate(
            'formBuilder.calculated.validation.stateTransitionOutputFieldOnly',
          );
        }

        if (outputTarget.type === 'calculated') {
          return this.translationService.translate(
            'formBuilder.calculated.validation.noCalculatedDependencies',
          );
        }
      }

      const toValues = new Set<string>();
      for (const rule of transitions) {
        const fromRaw = rule?.from ?? rule?.label ?? '';
        const fromValues = Array.isArray(fromRaw)
          ? fromRaw.map((value: unknown) => String(value ?? ''))
          : String(fromRaw)
              .split(',')
              .map((value) => value.trim());
        const toValue = String(rule?.to ?? rule?.value ?? '').trim();

        if (fromValues.length === 0 || fromValues.every((value: string) => value === '')) {
          return this.translationService.translate(
            'formBuilder.calculated.validation.transitionFromRequired',
          );
        }

        if (toValue === '') {
          return this.translationService.translate(
            'formBuilder.calculated.validation.transitionToRequired',
          );
        }

        if (toValues.has(toValue)) {
          return this.translationService.translate(
            'formBuilder.calculated.validation.transitionDuplicateToWarning',
          );
        }

        toValues.add(toValue);
      }

      return null;
    }

    return this.translationService.translate(
      'formBuilder.calculated.validation.calculationKindRequired',
    );
  }

  private validateVisibilityCondition(): string | null {
    const field = this.field();
    if (!field || field.visibleWhenMode !== 'show_when_value') {
      return null;
    }

    const visibleWhen = field.visibleWhen;
    const fieldId = typeof visibleWhen?.fieldId === 'string' ? visibleWhen.fieldId : '';

    if (!fieldId) {
      return this.translationService.translate('formBuilder.visibility.validation.fieldRequired');
    }

    if (fieldId === field.id) {
      return this.translationService.translate('formBuilder.visibility.validation.selfReference');
    }

    const targetExists = this.availableFields.some((candidate) => candidate.id === fieldId);
    if (!targetExists) {
      return this.translationService.translate('formBuilder.visibility.validation.fieldRequired');
    }

    const knownOptions = (
      this.configFormOptions.formState!['visibilityValueOptionsByFieldId'] as Record<
        string,
        { value: string; label: string }[]
      >
    )?.[fieldId];
    const hasKnownOptions = Array.isArray(knownOptions) && knownOptions.length > 0;

    let values: string[];
    if (hasKnownOptions) {
      const raw = visibleWhen?.selectedValues;
      values = Array.isArray(raw)
        ? raw.map((v: unknown) => String(v ?? '')).filter((v: string) => v !== '')
        : [];
    } else {
      const valuesRaw = visibleWhen?.values;
      values = Array.isArray(valuesRaw)
        ? valuesRaw.map((v: unknown) => String(v ?? '')).filter((v: string) => v !== '')
        : typeof valuesRaw === 'string'
          ? valuesRaw
              .split(',')
              .map((v: string) => v.trim())
              .filter((v: string) => v !== '')
          : [];
    }

    if (values.length === 0) {
      return this.translationService.translate('formBuilder.visibility.validation.valuesRequired');
    }

    return null;
  }

  /**
   * Recursively collect paths of all invalid AbstractControls within a FormGroup or FormArray.
   * Used to emit helpful console diagnostics when save is blocked by form invalidity.
   */
  private collectInvalidControls(control: AbstractControl, path: string, result: string[]): void {
    if (control instanceof FormGroup) {
      for (const [key, child] of Object.entries(control.controls)) {
        const childPath = path ? `${path}.${key}` : key;
        if (child.invalid) {
          result.push(childPath);
          this.collectInvalidControls(child, childPath, result);
        }
      }
    } else if (control instanceof FormArray) {
      control.controls.forEach((child, i) => {
        const childPath = `${path}[${i}]`;
        if (child.invalid) {
          result.push(childPath);
          this.collectInvalidControls(child, childPath, result);
        }
      });
    }
  }
}
